// Report an incident from wherever it was noticed — a client's record, a
// visit on the desk, or the Incident Log itself. Coordinators and nurses can
// file; only managers close one or record the external report.
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { INCIDENT_TYPES, REPORTABLE_TYPES, ACTIONS, SUBJECTS, fileIncident } from '../../lib/incidents';

const field = { width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
const label = { fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
const nowLocal = () => format(new Date(), "yyyy-MM-dd'T'HH:mm");

export default function IncidentReportModal({ client, staff, subject = 'client', source = 'manual', onClose, onFiled }) {
  const { profile } = useAuth();
  const [clients, setClients] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    subject_type: subject,
    client_id: client?.id || '',
    staff_id: staff?.id || '',
    incident_date: format(new Date(), 'yyyy-MM-dd'),
    discovered_at: nowLocal(),
    incident_type: '',
    category: 'none',
    description: '',
    action_taken: '',
    follow_up_date: '',
    reportable: false,
    source,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!client) supabase.from('clients').select('id, client_id, first_name, last_name').order('last_name').then(({ data }) => setClients(data || []));
    if (!staff) supabase.from('staff').select('id, first_name, last_name, position_code').eq('status', 'active').order('last_name').then(({ data }) => setStaffList(data || []));
  }, [client, staff]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const inc = await fileIncident(form, profile);
      onFiled && onFiled(inc);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 660 }}>
        <div className="modal-header"><span>Report an incident</span><button className="btn btn-outline btn-sm" onClick={onClose}>Close</button></div>
        <div className="modal-body">
          {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={label}>Mainly about</div>
              <select style={field} value={form.subject_type} onChange={e => set('subject_type', e.target.value)}>
                {SUBJECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div style={label}>What happened</div>
              <select style={field} value={form.incident_type}
                onChange={e => setForm(f => ({ ...f, incident_type: e.target.value, reportable: f.reportable || REPORTABLE_TYPES.has(e.target.value) }))}>
                <option value="">Choose…</option>
                {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Both links, always. An event at a client's home involving a caregiver is both. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div>
              <div style={label}>Client involved{form.subject_type === 'client' ? '' : ' (if any)'}</div>
              {client ? <div style={{ ...field, background: 'var(--bg3)' }}>{client.last_name}, {client.first_name} · {client.client_id}</div> : (
                <select style={field} value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                  <option value="">None</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name} · {c.client_id}</option>)}
                </select>
              )}
            </div>
            <div>
              <div style={label}>Caregiver involved{form.subject_type === 'staff' ? '' : ' (if any)'}</div>
              {staff ? <div style={{ ...field, background: 'var(--bg3)' }}>{staff.last_name}, {staff.first_name}</div> : (
                <select style={field} value={form.staff_id} onChange={e => set('staff_id', e.target.value)}>
                  <option value="">None</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.last_name}, {s.first_name} · {s.position_code}</option>)}
                </select>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div><div style={label}>When it happened</div><input type="date" style={field} value={form.incident_date} onChange={e => set('incident_date', e.target.value)} /></div>
            <div><div style={label}>When we found out</div><input type="datetime-local" style={field} value={form.discovered_at} onChange={e => set('discovered_at', e.target.value)} />
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>The 24-hour reporting clock runs from here.</div></div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={label}>What happened, in full</div>
            <textarea style={{ ...field, minHeight: 90 }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Who was present, what occurred, condition of the client, what was done immediately." />
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={label}>Immediate action taken</div>
            <textarea style={{ ...field, minHeight: 56 }} value={form.action_taken} onChange={e => set('action_taken', e.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div>
              <div style={label}>Disciplinary outcome</div>
              <select style={field} value={form.category} disabled={!form.staff_id} onChange={e => set('category', e.target.value)}>
                {ACTIONS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>{form.staff_id ? 'Only if this became a disciplinary matter.' : 'Name a caregiver to record an outcome.'}</div>
            </div>
            <div><div style={label}>Follow-up by</div><input type="date" style={field} value={form.follow_up_date} onChange={e => set('follow_up_date', e.target.value)} /></div>
          </div>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: form.reportable ? 'var(--warn-bg)' : 'var(--bg2)', border: `0.5px solid ${form.reportable ? 'var(--warn-border)' : 'var(--border)'}` }}>
            <input type="checkbox" checked={form.reportable} onChange={e => set('reportable', e.target.checked)} style={{ marginTop: 2 }} />
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>
              <b>Reportable to DCH within 24 hours.</b> Ticked automatically for client safety events, injuries, medication errors, abuse or neglect allegations, elopement, hospitalisation, falls and deaths. The deadline runs from when we found out.
            </span>
          </label>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? 'Filing…' : 'File incident'}</button>
        </div>
      </div>
    </div>
  );
}
